import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReservationCommercialPanel } from '@/modules/commercial/components';
import { ReservationLogisticsPanel } from '@/modules/logistics/components';
import {
  ConflictAlert,
  ReservationStatusBadge,
} from '@/modules/reservations/components';
import { useReservationAvailability } from '@/modules/reservations/hooks';
import {
  cancelReservation,
  getReservation,
} from '@/modules/reservations/services';
import { formatReservationApiError } from '@/services/api/reservations.api';
import { listItems } from '@/modules/inventory/services';
import { RESERVATION_STATUS_LABELS } from '@/modules/reservations/types';
import { modulePaths } from '@/routes/paths';
import { EntityAuditTrail } from '@/shared/components/EntityAuditTrail';
import { useAuth } from '@/shared/hooks/useAuth';
import type { InventoryItem } from '@/modules/inventory/types';

export function ReservationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('reservations.write');
  const { result, check } = useReservationAvailability();

  const [reservation, setReservation] = useState<Awaited<
    ReturnType<typeof getReservation>
  >>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void Promise.all([getReservation(id), listItems()])
      .then(([res, allItems]) => {
        if (!res) {
          setError('Reserva no encontrada.');
          return;
        }
        setReservation(res);
        setItems(allItems);
        if (
          res.status === 'confirmed' ||
          res.status === 'in_progress'
        ) {
          void check({
            inventoryItemIds: res.inventoryItemIds,
            startDate: res.startDate,
            endDate: res.endDate,
            excludeReservationId: res.id,
          });
        }
      })
      .catch(() => setError('Error al cargar la reserva.'))
      .finally(() => setLoading(false));
  }, [id, check]);

  async function handleCancel() {
    if (!id || !canWrite) return;
    const ok = window.confirm(
      '¿Cancelar esta reserva? Los artículos quedarán disponibles para otras reservas en ese rango.',
    );
    if (!ok) return;
    setCancelling(true);
    try {
      await cancelReservation(id);
      void navigate(modulePaths.reservations, { replace: true });
    } catch (err) {
      setError(formatReservationApiError(err));
    } finally {
      setCancelling(false);
    }
  }

  if (loading) return <p className="auth-muted">Cargando…</p>;
  if (!reservation) {
    return (
      <section>
        <p className="auth-error">{error ?? 'Reserva no encontrada.'}</p>
        <Link to={modulePaths.reservations}>← Volver</Link>
      </section>
    );
  }

  const itemName = (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    return item ? `${item.sku} — ${item.name}` : itemId;
  };

  const hasExternalConflicts =
    (result?.conflicts.length ?? 0) > 0 && reservation.status !== 'cancelled';

  return (
    <section className="reservation-detail">
      <p>
        <Link to={modulePaths.reservations}>← Volver al listado</Link>
      </p>

      <div className="inventory-page-header">
        <h1>{reservation.title}</h1>
        <ReservationStatusBadge status={reservation.status} />
      </div>

      {canWrite && reservation.status !== 'cancelled' ? (
        <div className="inventory-actions">
          <Link
            to={`${modulePaths.reservations}/${reservation.id}/editar`}
            className="auth-button inventory-page-header__btn"
          >
            Editar
          </Link>
          <button
            type="button"
            className="auth-button auth-button--secondary"
            disabled={cancelling}
            onClick={() => void handleCancel()}
          >
            {cancelling ? 'Cancelando…' : 'Cancelar reserva'}
          </button>
        </div>
      ) : null}

      <dl className="reservation-detail-grid">
        <dt>Cliente</dt>
        <dd>{reservation.clientName || '—'}</dd>
        <dt>Desde</dt>
        <dd>{reservation.startDate}</dd>
        <dt>Hasta</dt>
        <dd>{reservation.endDate}</dd>
        <dt>Estado</dt>
        <dd>{RESERVATION_STATUS_LABELS[reservation.status]}</dd>
        <dt>Descripción</dt>
        <dd>{reservation.description || '—'}</dd>
        <dt>Notas</dt>
        <dd>{reservation.notes || '—'}</dd>
        <dt>Creada</dt>
        <dd>{reservation.createdAt.toLocaleString('es-AR')}</dd>
        <dt>Actualizada</dt>
        <dd>{reservation.updatedAt.toLocaleString('es-AR')}</dd>
      </dl>

      <h2>Artículos asociados</h2>
      {reservation.inventoryItemIds.length === 0 ? (
        <p className="auth-muted">Sin artículos.</p>
      ) : (
        <ul>
          {reservation.inventoryItemIds.map((itemId) => (
            <li key={itemId}>
              <Link to={`${modulePaths.inventory}/${itemId}`}>
                {itemName(itemId)}
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasExternalConflicts ? (
        <>
          <h2>Conflictos detectados</h2>
          <ConflictAlert conflicts={result?.conflicts ?? []} />
          <p className="auth-muted">
            Hay solapamiento con otra reserva bloqueante. Revisá fechas o ítems.
          </p>
        </>
      ) : null}

      <ReservationCommercialPanel reservationId={reservation.id} />

      <ReservationLogisticsPanel reservationId={reservation.id} />

      <EntityAuditTrail entityType="reservation" entityId={reservation.id} />

      {error ? <p className="auth-error">{error}</p> : null}
    </section>
  );
}
